/**
 * ClientSubscriptions – Recurring invoice templates for a client.
 * Uses GET/POST/DELETE /api/subscriptions/
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import api, { enhancedApi } from '../api';

const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi-annually', label: 'Semi-annually' },
  { value: 'annually', label: 'Annually' },
];

const PATTERN_TO_LABEL = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
};

const ClientSubscriptions = ({ clientId, clientName }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [canceling, setCanceling] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [subToCancel, setSubToCancel] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historySub, setHistorySub] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [form, setForm] = useState({
    client_id: clientId || '',
    amount: '',
    frequency: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    description: '',
    auto_send: false,
  });
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (historyOpen && historySub?.id) {
      setHistoryLoading(true);
      setHistoryError(null);
      api.get(`/api/subscriptions/${historySub.id}/history/`)
        .then((res) => {
          setHistoryData(res.data?.history ?? []);
        })
        .catch((err) => {
          setHistoryError(err.response?.data?.error || 'Failed to load history.');
          setHistoryData([]);
        })
        .finally(() => setHistoryLoading(false));
    }
  }, [historyOpen, historySub?.id]);

  const handleHistoryClick = (sub) => {
    setHistorySub(sub);
    setHistoryOpen(true);
  };

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = clientId ? { client_id: clientId } : {};
      const res = await api.get('/api/subscriptions/', { params });
      const data = res.data?.subscriptions ?? [];
      setSubscriptions(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load subscriptions.');
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await enhancedApi.getClients();
      const data = Array.isArray(res.data) ? res.data : (res.data?.results || res.data?.clients || []);
      setClients(data.map((c) => ({
        id: c.id,
        name: c.display_name || c.primary_contact?.display_name || c.name || `Client #${c.id}`,
      })));
    } catch (err) {
      setClients([]);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, [clientId]);

  useEffect(() => {
    if (createOpen) {
      fetchClients();
      setForm((f) => ({
        ...f,
        client_id: clientId || '',
        amount: '',
        frequency: 'monthly',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        description: '',
        auto_send: false,
      }));
      setFormError(null);
    }
  }, [createOpen, clientId]);

  const handleCreate = async () => {
    setFormError(null);
    if (!form.client_id) {
      setFormError('Client is required.');
      return;
    }
    const amountNum = parseFloat(form.amount);
    if (isNaN(amountNum) || amountNum < 1) {
      setFormError('Amount must be at least 1.');
      return;
    }
    if (!form.start_date) {
      setFormError('Start date is required.');
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    if (form.start_date < today) {
      setFormError('Start date cannot be in the past.');
      return;
    }
    if (form.end_date && form.end_date <= form.start_date) {
      setFormError('End date must be after start date.');
      return;
    }

    setCreating(true);
    try {
      await api.post('/api/subscriptions/', {
        client_id: form.client_id,
        amount: amountNum,
        frequency: form.frequency,
        start_date: form.start_date,
        end_date: form.end_date || undefined,
        description: form.description || undefined,
        auto_send: form.auto_send,
      });
      setCreateOpen(false);
      fetchSubscriptions();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to create recurring invoice.');
    } finally {
      setCreating(false);
    }
  };

  const handleCancelClick = (sub) => {
    setSubToCancel(sub);
    setCancelConfirmOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!subToCancel) return;
    setCanceling(subToCancel.id);
    try {
      await api.delete(`/api/subscriptions/${subToCancel.id}/`);
      setCancelConfirmOpen(false);
      setSubToCancel(null);
      fetchSubscriptions();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel recurring invoice.');
    } finally {
      setCanceling(null);
    }
  };

  const formatAmount = (amt) =>
    typeof amt === 'number'
      ? amt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '0.00';

  const formatFrequency = (pattern) => PATTERN_TO_LABEL[pattern] || pattern || '—';

  if (error) return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Recurring Invoices</Typography>
          <Chip label={subscriptions.length} size="small" />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchSubscriptions} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            Set Up Recurring Invoice
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : subscriptions.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <ReceiptIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No recurring invoices yet.</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ mt: 1.5 }}
          >
            Set Up First Recurring Invoice
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.default' }}>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Frequency</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Next Invoice Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptions.map((sub) => (
                <TableRow key={sub.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {sub.description || sub.invoice_number || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ${formatAmount(sub.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={formatFrequency(sub.recurrence_pattern)} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {sub.recurrence_next_date
                        ? new Date(sub.recurrence_next_date).toLocaleDateString()
                        : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={sub.status || 'Active'} size="small" color="success" variant="outlined" />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="History">
                      <IconButton
                        size="small"
                        onClick={() => handleHistoryClick(sub)}
                        sx={{ color: 'text.secondary' }}
                      >
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel recurring invoice">
                      <IconButton
                        size="small"
                        color="error"
                        disabled={canceling === sub.id}
                        onClick={() => handleCancelClick(sub)}
                      >
                        {canceling === sub.id ? <CircularProgress size={16} /> : <CancelIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Set Up Recurring Invoice Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set Up Recurring Invoice</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {formError && (
              <Alert severity="error" onClose={() => setFormError(null)}>
                {formError}
              </Alert>
            )}
            <FormControl fullWidth required disabled={!!clientId}>
              <InputLabel>Client</InputLabel>
              <Select
                value={form.client_id}
                label="Client"
                onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value }))}
              >
                <MenuItem value="">Select client</MenuItem>
                {(
                  clientId
                    ? [{ id: clientId, name: clientName || clients.find((c) => c.id === clientId)?.name || `Client #${clientId}` }, ...clients.filter((c) => c.id !== clientId)]
                    : clients
                ).map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Amount"
              type="number"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              fullWidth
              required
              inputProps={{ min: 1, step: 0.01 }}
              placeholder="99.00"
            />
            <FormControl fullWidth>
              <InputLabel>Frequency</InputLabel>
              <Select
                value={form.frequency}
                label="Frequency"
                onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))}
              >
                {FREQUENCY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Start date"
              type="date"
              value={form.start_date}
              onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
              fullWidth
              required
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End date (optional)"
              type="date"
              value={form.end_date}
              onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
              fullWidth
              inputProps={{ min: form.start_date }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
              placeholder="e.g. Monthly Tax Compliance"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.auto_send}
                  onChange={(e) => setForm((f) => ({ ...f, auto_send: e.target.checked }))}
                  color="primary"
                />
              }
              label="Auto-send invoices when generated"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {creating ? 'Creating…' : 'Set Up Recurring Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History dialog */}
      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          Generated Invoices
          {historySub && (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, mt: 0.5 }}>
              {historySub.description || historySub.invoice_number || `#${historySub.id}`}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {historyError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setHistoryError(null)}>
              {historyError}
            </Alert>
          )}
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : historyData.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No invoices generated yet from this recurring schedule.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Invoice #</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyData.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Button
                          size="small"
                          variant="text"
                          sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                          onClick={() => navigate('/billing/invoices')}
                        >
                          {row.invoice_number}
                        </Button>
                      </TableCell>
                      <TableCell>
                        {row.invoice_date ? new Date(row.invoice_date).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>
                        {row.due_date ? new Date(row.due_date).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip label={row.status || '—'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        ${formatAmount(row.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel confirmation dialog */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={() => !canceling && setCancelConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>Cancel Recurring Invoice</DialogTitle>
        <DialogContent>
          <Typography>
            Cancel this recurring invoice? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmOpen(false)} disabled={!!canceling}>
            No, keep it
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelConfirm}
            disabled={!!canceling}
            startIcon={canceling ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            {canceling ? 'Canceling…' : 'Yes, cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientSubscriptions;
